// SNOWCLUB-LAUNCH-REMAINING-PARITY-001 · RANKED WHISTLER ROUTE PARITY — the served, route-specific body
// of a /mountain-whistler/<route> page.
//
// SERVER COMPONENT, in the crawlable [data-sg-server-layer] block of the export branch, so every word
// and every link exists in the delivered HTML with JS off. Each block carries `data-sg-paint`, which is
// the ONE marker ServerContentPaint clones into the design's own scroller once the design has painted —
// the same render contract the three transport-information families already use (painted-content.ts).
// Nothing is a second source of words: the clone is built from these served elements.
//
// LINKS ARE REAL ANCHORS, host-relative. No absolute snowbus.com href and no retired /whistler-bus/*
// path can come out of here: every href is either a registry-declared route path or one of the two
// Whistler schedule pages (whistler-route-content.ts), and the focused test asserts that set.

import type { RouteContent, RouteLink } from "@/lib/whistler-route-content";

const A = ({ l }: { l: RouteLink }) => <a href={l.href}>{l.label}</a>;

function LinkRun({ links }: { links?: RouteLink[] }) {
  if (!links?.length) return null;
  return (
    <>
      {" "}
      {links.map((l, i) => (
        <span key={l.href}>
          {i > 0 ? " · " : "See: "}
          <A l={l} />
        </span>
      ))}
    </>
  );
}

export default function WhistlerRouteServerContent({ content }: { content: RouteContent }) {
  return (
    <section
      data-sg-route-content={content.slug}
      data-sg-route-kind={content.kind}
      data-sg-route-availability={content.availability}
      aria-label={content.heading}
    >
      <h2 data-sg-paint="">{content.heading}</h2>
      {content.sections.map((s) => (
        <div key={s.id} data-sg-route-section={s.id}>
          <h3 data-sg-paint="">{s.heading}</h3>
          {s.paragraphs.filter(Boolean).map((p, i) => (
            <p key={i} data-sg-paint="">
              {p}
              {i === s.paragraphs.length - 1 ? <LinkRun links={s.links} /> : null}
            </p>
          ))}
        </div>
      ))}
      <div data-sg-route-section="related">
        <h3 data-sg-paint="">Related Whistler routes</h3>
        <ul>
          {content.related.map((l) => (
            <li key={l.href} data-sg-paint="">
              <A l={l} />
            </li>
          ))}
        </ul>
      </div>
      <div data-sg-route-section="faq">
        <h3 data-sg-paint="">Frequently asked questions</h3>
        {content.faqs.map((f) => (
          <div key={f.question}>
            <h4 data-sg-paint="">{f.question}</h4>
            <p data-sg-paint="">
              {f.answer}
              <LinkRun links={f.links} />
            </p>
          </div>
        ))}
      </div>
      <nav aria-label="Breadcrumb" data-sg-route-section="breadcrumb">
        <ol>
          {content.breadcrumbs.map((c, i) => (
            <li key={c.href}>{i === content.breadcrumbs.length - 1 ? <span aria-current="page">{c.label}</span> : <A l={c} />}</li>
          ))}
        </ol>
      </nav>
    </section>
  );
}
